// ============================================================================
// USER MANAGEMENT HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { userManagementService } from 'api/services/userManagementService';
import type { UserCacheRecord, AdminResetPasswordRequest } from 'api/types/userManagement.types';

export const useUserManagement = () => {
  const [data, setData] = useState<UserCacheRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userManagementService.getUsers();
      setData((response.data || []).map((u: any) => ({ ...u, id: u.userId })));
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return { data, loading, error, refetch: fetchUsers };
};

export const useAdminResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const resetPassword = useCallback(async (userId: string, payload: AdminResetPasswordRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await userManagementService.resetPassword(userId, payload);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { resetPassword, loading, error };
};
